import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {CollectionCarousel} from '@bitstillery/common/components/portal'
import {PlausibleCartSource} from '@bitstillery/common/lib/utils'
import {CollectionProxy} from '@bitstillery/common/lib/collection'
import {unchecked} from '@bitstillery/common/lib/unchecked'

import {CollectionProduct} from '@/components/collection/product'
import {load_product_photos} from '@/models/offer'
import type {CartOfferItem} from '@/models/cart'
import {$s} from '@/app'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const productsMeta = {
    tier: 'data',
    summary: 'Carousel of product cards for a set of offer items.',
    when_to_use:
        'Render inside an `AsyncData` whose `source` is an offer-items endpoint — that endpoint chooses which products appear. Flip the `source` on `portal_session_authenticated()` to serve priced items to members and public pallet-deal rows to guests (e.g. `portal/offer-items/spotlight/collection-view` vs `portal/pallet-deals`); either row shape works. Heavy widget; use once per page. Requires runtime collection/cart context on the portal.',
    example: `
    <AsyncData body={{limit: 12, offer_item_type: 'NEW_ARRIVALS'}} method="post" source="portal/offer-items">
        {(rows) => <Products items={rows.portal_offer_items} title="New arrivals" />}
    </AsyncData>
    `,
    // The real source is an authenticated POST, which is a 403 on the anonymous catalog overview; these
    // rows carry the fields a card reads so the carousel shows itself without any request. No image
    // locations — a made-up path would only be a broken request of a different kind.
    showcase: `
    <Products
        items={[
            {artkey: 1, bottle_alcohol_percentage: 40, bottle_volume: 70, case_article_code: 'GLF12-6', case_artkey: 1, case_customs_status: 'T1', case_number_of_bottles: 6, currency: 'EUR', list_price: '186.00', offer_item_type: 'stock', offer_type: 'purchase', original_price_per_case: '186.00', product_category: 'whisky', product_name: 'Glenfiddich 12y'},
            {artkey: 2, bottle_alcohol_percentage: 37.5, bottle_volume: 100, case_article_code: 'BAC-12', case_artkey: 2, case_customs_status: 'T2', case_number_of_bottles: 12, currency: 'EUR', list_price: '132.00', offer_item_type: 'stock', offer_type: 'spotlight', original_price_per_case: '148.00', product_category: 'rum', product_name: 'Bacardi Carta Blanca'},
            {artkey: 3, bottle_alcohol_percentage: 40, bottle_volume: 70, case_article_code: 'HEN-6', case_artkey: 3, case_customs_status: 'T1', case_number_of_bottles: 6, currency: 'EUR', list_price: '318.00', offer_item_type: 'tbo', offer_type: 'purchase', original_price_per_case: '318.00', product_category: 'cognac', product_name: 'Hennessy VS'},
            {artkey: 4, bottle_alcohol_percentage: 40, bottle_volume: 70, case_article_code: 'ABS-12', case_artkey: 4, case_customs_status: 'T2', case_number_of_bottles: 12, currency: 'EUR', list_price: '156.00', offer_item_type: 'stock', offer_type: 'purchase', original_price_per_case: '156.00', product_category: 'vodka', product_name: 'Absolut Blue'},
        ]}
        title="New arrivals"
    />
    `,
} satisfies CmsComponentMeta

/**
 * A row from an offer-items `source`. Either shape works: a full offer item (`artkey` + `case_artkey`),
 * or a public pallet-deal row (only `offer_item_artkey`) — the widget fills the missing keys.
 */
export type ProductRow = Record<string, unknown> & {
    artkey?: number | undefined
    case_artkey?: number | undefined
    offer_item_artkey?: number
}

export interface ProductsAttrs {
    /** Offer items to render — the `rows` from an `AsyncData` offer-items `source`. */
    items: ProductRow[]
    /** Carousel heading. */
    title?: string
    /** Optional deep link to where these items live in the shop (e.g. `/shop?offer=…`). Used by the
     *  heading and by each card's title/specs — the photo and cart controls keep their own actions. */
    title_link?: string
    /** Home funnel only: match Popular categories title + panel; edge scroll buttons. */
    layout?: 'default' | 'funnel'
}

/**
 * Renders a carousel of product cards from a list of offer items. Loading is left to an `AsyncData`
 * wrapper (which picks the endpoint and carries the rows across SSR); this component only renders them.
 */
export class Products extends MithrilComponent<ProductsAttrs> {
    private readonly collection = new CollectionProxy<CartOfferItem>()
    /** Fingerprint of the last-synced items — avoids re-splicing/re-fetching on every redraw. */
    private synced_key: string | null = null

    /** Public pallet-deal rows carry only `offer_item_artkey`; give every row the `artkey`/`case_artkey` cards need. */
    private normalize(items: ProductRow[]): ProductRow[] {
        // A not-yet-generated price list answers offer-items with a 404 error body rather than rows;
        // that leaks through `AsyncData` as a non-array, so render nothing until real rows arrive.
        if (!Array.isArray(items)) {
            return []
        }
        return items.map((row) => {
            if (row.artkey != null && row.case_artkey != null) {
                return row
            }
            const artkey = row.artkey ?? row.offer_item_artkey
            return {...row, artkey, case_artkey: row.case_artkey ?? artkey}
        })
    }

    /** Mirror `items` into the collection and, for authenticated users, load their product imagery. */
    private sync(raw_items: ProductRow[] | undefined): Promise<void> | void {
        // `AsyncData` hands us `undefined` while the source isn't ready yet (e.g. the relation's price list is
        // still generating). Flag the collection as pending so the carousel shows its waiting widget, and wait
        // for `AsyncData` to poll and re-supply real rows.
        if (!Array.isArray(raw_items)) {
            this.synced_key = null
            this.collection.state.content_state = 'pending'
            this.collection.state.items.splice(0, this.collection.state.items.length)
            return
        }
        // The cards read every row as an offer item; a public pallet-deal row only reaches the guest card,
        // which reads just the fields such a row has.
        const items = unchecked<CartOfferItem[]>(this.normalize(raw_items))
        const key = items.map((i) => i.artkey).join(',')
        if (key === this.synced_key) {
            return
        }
        this.synced_key = key
        this.collection.state.content_state = 'available'
        const current = this.collection.state.items
        current.splice(0, current.length, ...items)

        // Product imagery comes from an authenticated endpoint; guests fall back to the row's category image.
        if (!$s.identity.token) {
            return
        }
        const load = load_product_photos(items.map((i) => i.artkey))
        // Returned so SSR awaits it; on the client, repaint once the photos arrive.
        if (globalThis.__SSR_MODE__) {
            return load
        }
        void load.then(() => m.redraw())
    }

    override oninit(vnode: m.Vnode<ProductsAttrs>) {
        return this.sync(vnode.attrs.items)
    }

    override onbeforeupdate(vnode: m.Vnode<ProductsAttrs>) {
        void this.sync(vnode.attrs.items)
    }

    view(vnode: m.Vnode<ProductsAttrs>) {
        const is_auth = Boolean($s.identity.token)
        const carousel_layout = vnode.attrs.layout === 'funnel' ? 'funnel' : 'default'

        return (
            <CollectionCarousel
                collection={this.collection}
                icon='offers'
                layout={carousel_layout}
                item={(cart_item, opts) => (
                    <CollectionProduct
                        collection={this.collection}
                        details_link={vnode.attrs.title_link}
                        global_index={opts.global_index}
                        guest={!is_auth}
                        key={cart_item.case_artkey}
                        offer_item={cart_item}
                        source={PlausibleCartSource.SPOTLIGHT}
                        variant='cart'
                        virtualize={false}
                    />
                )}
                title={vnode.attrs.title}
                title_link={vnode.attrs.title_link}
            />
        )
    }
}
